/**
 * Audit Trail Page (Enterprise Only)
 */

import React, { useEffect, useState } from 'react';
import { Card, Table, Select, TextInput, Badge, Pagination } from 'flowbite-react';
import { HiSearch, HiFilter, HiDownload } from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { enterpriseApi } from '../api/enterprise';
import type { AuditLogEntry } from '../types';

const ACTION_COLORS: Record<string, string> = {
  create: 'success',
  update: 'info',
  delete: 'failure',
  invite: 'purple',
  accept: 'success',
  reject: 'failure',
  promote: 'info',
  demote: 'warning',
  reinforce: 'success',
  weaken: 'warning',
};

const AuditTrail: React.FC = () => {
  const { hasFeature } = useStatus();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    action: '',
    resourceType: '',
    startDate: '',
    endDate: '',
  });

  // Check feature access
  if (!hasFeature('audit_trail')) {
    return (
      <Card className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Enterprise Feature</h2>
        <p className="text-gray-500 mb-4">
          Audit trail is available on the Enterprise plan.
        </p>
        <a
          href="/subscriptions"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-mindmeld-primary rounded-md hover:bg-blue-700"
        >
          Upgrade to Enterprise
        </a>
      </Card>
    );
  }

  useEffect(() => {
    loadAuditLog();
  }, [page, filters]);

  const loadAuditLog = async () => {
    setIsLoading(true);
    try {
      const response = await enterpriseApi.getAuditLog({
        page,
        pageSize: 20,
        ...filters,
      });

      if (response.success && response.data) {
        setEntries(response.data.records);
        setTotalPages(Math.ceil(response.data.total / response.data.pageSize));
      } else {
        setError(response.error || 'Failed to load audit log');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    // TODO: Implement CSV export
    alert('Export functionality coming soon');
  };

  const formatValue = (value: any) => {
    if (!value) return '-';
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Audit Trail</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track all changes and actions in your organization
          </p>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <HiDownload className="mr-2 h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
            <Select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            >
              <option value="">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="invite">Invite</option>
              <option value="accept">Accept</option>
              <option value="reject">Reject</option>
              <option value="reinforce">Reinforce</option>
              <option value="weaken">Weaken</option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
            <Select
              value={filters.resourceType}
              onChange={(e) => setFilters({ ...filters, resourceType: e.target.value })}
            >
              <option value="">All Types</option>
              <option value="project">Project</option>
              <option value="invariant">Invariant</option>
              <option value="collaborator">Collaborator</option>
              <option value="subscription">Subscription</option>
              <option value="knowledge">Knowledge</option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <TextInput
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <TextInput
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </div>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md">{error}</div>
      )}

      {/* Audit Log Table */}
      <Card>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mindmeld-primary"></div>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No audit log entries found
          </div>
        ) : (
          <>
            <Table>
              <Table.Head>
                <Table.HeadCell>Timestamp</Table.HeadCell>
                <Table.HeadCell>Actor</Table.HeadCell>
                <Table.HeadCell>Action</Table.HeadCell>
                <Table.HeadCell>Resource</Table.HeadCell>
                <Table.HeadCell>Details</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {entries.map((entry) => (
                  <Table.Row key={entry.audit_id} className="bg-white">
                    <Table.Cell>
                      <span className="text-sm text-gray-500">
                        {new Date(entry.created_at).toLocaleString()}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm font-medium text-gray-900">
                        {entry.actor_email}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={ACTION_COLORS[entry.action] || 'gray'}>
                        {entry.action}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <div>
                        <span className="text-sm text-gray-900">{entry.resource_type}</span>
                        <br />
                        <span className="text-xs text-gray-500">{entry.resource_id}</span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="max-w-xs">
                        {entry.old_value && (
                          <div className="text-xs text-gray-500">
                            <span className="font-medium">Before:</span>{' '}
                            <span className="truncate">{formatValue(entry.old_value)}</span>
                          </div>
                        )}
                        {entry.new_value && (
                          <div className="text-xs text-gray-500">
                            <span className="font-medium">After:</span>{' '}
                            <span className="truncate">{formatValue(entry.new_value)}</span>
                          </div>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-4">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default AuditTrail;
